import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AddressesLookup, useLocation } from '@vmos2/location';
import { Alert, Modal, Text } from '@vmos2/design-system';
import isEmpty from 'lodash/isEmpty';
import { useDeviceSize } from '@vmos2/providers';
import styled from '@emotion/styled';

import { AddressDetails } from './AddressDetails';
import { LocationPin } from './LocationPin';
import { DeliveryInstructions } from './DeliveryInstructions';
import { FooterButton } from './FooterButton';
import { useAddressWizard } from './useAddressWizard';
import { ModalSteps } from './constants';

type Props = {
  Form: Node,
  isOpen: boolean,
  address?: {
    address1?: string,
    address2?: string,
    town?: string,
    country?: string,
    postcode?: string,
    label?: string,
    location?: {
      latitude: number,
      longitude: number,
    }
  },
  onClose: Function,
  onSubmit: Function,
  handleAvailableForDelivery?: Function,
};

export const SearchAddressModal = ({
  Form,
  isOpen,
  address: initialAddress,
  onClose,
  onSubmit,
  handleAvailableForDelivery
}: Props) => {
  const { isMobile } = useDeviceSize();
  const { t } = useTranslation();
  const { setSaveToLocalStorage } = useLocation();
  const {
    step,
    setStep,
    address,
    errors,
    setErrors,
    hasResponseError,
    setResponseError,
    updateAddress,
    handleAddressSelect,
  } = useAddressWizard(initialAddress);

  useEffect(() => {
    setSaveToLocalStorage(false);
  }, [setSaveToLocalStorage]);

  const handleClose = useCallback(() => {
    onClose();
    setStep(ModalSteps.SEARCH);
  }, [onClose]);

  const handleBack = useCallback(() => {
    if (step < ModalSteps.DETAILS || step === ModalSteps.DETAILS && initialAddress) {
      handleClose();
      return;
    }

    setStep(prev => prev - 1);
  }, [step, initialAddress, handleClose]);

  const handleSubmit = useCallback(async () => {
    try {
      if(isEmpty(address.label)) {
        address.label = address.address1;
      }
      await onSubmit(address);
      setStep(ModalSteps.SEARCH);
    } catch (error) {
      setResponseError(true);
    }
  }, [address, onSubmit]);

  const stepConfig = useMemo(() => ({
    [ModalSteps.SEARCH]: {
      title: t('delivery:searchAddress'),
      content: (
        <AddressesLookup
          preselectAddress={false}
          autoFocus
          disableUseMyLocation
          alertIfAddressNotFound
          handleSelectAddress={handleAddressSelect}
        />
      ),
      showFooter: false,
    },
    [ModalSteps.DETAILS]: {
      title: t('delivery:addressDetails'),
      content: (
        <AddressDetails
          Form={Form}
          address={address}
          onChange={updateAddress}
          setErrors={setErrors}
        />
      ),
      buttonText: t('delivery:confirmAddress'),
      onNext: () => setStep(ModalSteps.PIN),
    },
    [ModalSteps.PIN]: {
      title: t('delivery:pinYourEntrance'),
      content: (
        <LocationPin
          position={address?.location}
          onChange={updateAddress}
          setErrors={setErrors}
          handleAvailableForDelivery={handleAvailableForDelivery}
        />
      ),
      buttonText: t('delivery:confirmPin'),
      onNext: () => setStep(ModalSteps.INSTRUCTIONS),
    },
    [ModalSteps.INSTRUCTIONS]: {
      title: t('delivery:instructions'),
      content: (
        <>
          {hasResponseError && (
            <Alert type="danger" mt={4} mb={6}>
              {t('delivery:addAddressError')}
            </Alert>
          )}
          <DeliveryInstructions
            Form={Form}
            address={{
              phoneNumber: address?.phoneNumber,
              deliveryInstructions: address?.deliveryInstructions,
            }}
            onChange={updateAddress}
            setErrors={setErrors}
          />
        </>
      ),
      buttonText: t('common:finish'),
      onNext: handleSubmit,
    },
  }), [t, address, errors, hasResponseError, handleSubmit, handleAddressSelect, updateAddress]);

  const currentStep = stepConfig[step];

  return (
    <Modal
      isOpen={isOpen}
      onBackButtonClick={handleBack}
      onRequestClose={handleClose}
      title={currentStep.title}
      size="full-height"
      height="660px"
      progressValue={step * 25}
      footerSections={1}
      footerRightSide={
        currentStep.showFooter !== false && (
          <FooterWrapper>
            {errors && step === ModalSteps.PIN && (
              <Text mb={2} level={5} bold type="danger">
                {t('delivery:pinDeliveryError')}
              </Text>
            )}
            <FooterButton
              onClick={currentStep.onNext}
              buttonText={currentStep.buttonText}
              isDisabled={errors}
            />
          </FooterWrapper>
        )
      }
      footerIgnoreThemeHeight
      isFullScreen={isMobile}
      isSticky={isMobile}
      mode="UserProfileModal"
    >
      {currentStep.content}
    </Modal>
  );
};

const FooterWrapper = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
